<template>
  <div>
    <navigation-main bg="true" />
    <div class="main-content">
      <main role="main" class="flex-grow-1 overflow-auto">
        <div id="content">
          <section class="page-section">
            <div class="container">
              <div class="row ">
                <div class="col-12">
                  <h1 class="text-black mb-0">
                    Benchmarks
                  </h1>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p>
                    We provide a benchmark suite together with an evaluation
                    server for MUSES. For proper benchmarking, the benchmarks use
                    the test set, for which the ground truth is withheld.
                    Authors can upload the results of their method on the test
                    set and get its respective quantitative performance. We
                    offer four benchmarks, these are namely 
                    <a href="#panopticSegmentation">Panoptic Segmentation</a>,
                    <a href="#uncertaintyAwarePanopticSegmentation">Uncertainty-Aware Panoptic Segmentation</a>,
                    <a href="#semanticSegmentation">Semantic Segmentation</a>
                    and 
                    <a href="#objectDetection">Object Detection</a>.
                  </p>
                  <p>
                    Authors can submit results either for the complete test set
                    comprising all weather and illumination conditions, or for any individual condition.
                    Accordingly, there are 7 separate leaderboards for each
                    benchmark, namely All, Daytime, Nighttime, Clear, Fog, Rain and Snow.
                  </p>
                  <p>
                    In order to submit your results, you first need to register.
                    After registration, please follow the instructions on the
                    <RouterLink to="/submit">submission page</RouterLink>.
                  </p>
                </div>
              </div>
              <div class="m-5"></div>
              <div class="row " id="panseg">
                <!-- Work around to account for navigation height-->
                <div class="scroll-to-element" id="panopticSegmentation"></div>
                <div class="col-12">
                  <h2 class="text-black mb-0">
                    Panoptic Segmentation
                  </h2>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p>
                    The third MUSES task is panoptic segmentation. The required output is a per-pixel assignment
                    to a label from the set of 19 semantic classes included in MUSES, with an additional assignment of pixels
                    that are predicted to belong to a "thing" class to distinct instances of this class in the image. 
                  </p>
                  <p>
                    Evaluation is performed using the standard panoptic quality (PQ) metric at the level of classes and
                    averaging over all classes. Average results over "things" classes (PQ things) and "stuff" classes (PQ stuff) are also reported. 
                  </p>
                </div>
              </div>
              <div class="row">
                <ResultTables
                  :tasks="tasksPanSeg"
                  :isPanoptic="true"
                  :additional-headers="additionalHeaders"
                  :has-code="true"
                ></ResultTables>
              </div>

              <div class="m-5"></div>
              <div class="row ">
                <div class="col-12">
                  <!-- Work around to account for navigation height-->
                  <div
                    class="scroll-to-element"
                    id="uncertaintyAwarePanopticSegmentation"
                  ></div>
                  <h2 class="text-black mb-0">
                    Uncertainty-Aware Panoptic Segmentation
                  </h2>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p>
                    The fourth MUSES task is uncertainty-aware panoptic
                    segmentation. The required outputs for each image are three:
                  </p>
                  <ol>
                    <li>A panoptic prediction, similar to standard panoptic segmentation.</li>
                    <li>A class level confidence map associated with the semantic prediction, with values ranging from 0 to 1.
                      This map is evaluated against the ternary difficulty maps.</li>
                    <li>An instance level confidence map, which assigns a confidence score between 0 and 1 to each pixel,
                      indicating the model's confidence that the pixel belongs to a specific instance of an object.
<!--                      For example, in an image with multiple cars, each car would be considered a separate instance. -->
<!--                      The confidence map would assign each pixel a score between 0 and 1, -->
<!--                      indicating how confident the model is that the pixel belongs to a specific car. -->
                      This map is also evaluated against the ternary difficulty maps.</li>
                  </ol>
                  <p>
                    Evaluation is performed using the uncertainty-aware panoptic quality (UPQ) metric,
                    an extension of panoptic quality. UPQ takes into account the panoptic prediction, ground truth,
                    and binary class and instance confidence predictions. If a model predicts all pixels as confident,
                    UPQ reduces to the standard PQ.
                  </p>
                  <p>
                    For a threshold-agnostic summary metric, a linear grid of 16x16 thresholds for class and instance
                    confidences is defined. UPQ is evaluated for each of the 256 threshold pairs, and the average value
                    is reported as AUPQ.
                  </p>
                </div>
              </div>
              <div class="row">
                <ResultTables
                  :tasks="tasksPansegWithConfidence"
                  :isUncertain="true"
                  :additional-headers="additionalHeaders"
                  :has-code="true"
                ></ResultTables>
              </div>

              <div class="m-5"></div>

              <div class="row " id="semSeg">
                <!-- Work around to account for navigation height-->
                <div class="scroll-to-element" id="semanticSegmentation"></div>
                <div class="col-12">
                  <h2 class="text-black mb-0">
                    Semantic Segmentation
                  </h2>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p>
                    The second MUSES task is semantic segmentation. The required
                    output is a per-pixel assignment to a label from the set of
                    19 semantic classes included in MUSES.
                  </p>
                  <p>
                    Evaluation is performed using the standard
                    intersection-over-union (IoU) metric, also known as Jaccard
                    index, at the level of classes, and averaging over all
                    classes.
                  </p>
                </div>
              </div>
              <div class="row">
                <ResultTables
                  :tasks="tasksSemseg"
                  :isSemantic="true"
                  :additional-headers="additionalHeaders"
                  :has-code="true"
                ></ResultTables>
              </div>
              <div class="m-5"></div>

              <div class="row " id="objdet">
                <!-- Work around to account for navigation height-->
                <div class="scroll-to-element" id="objectDetection"></div>
                <div class="col-12">
                  <h2 class="text-black mb-0">
                    Object Detection
                  </h2>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p>
                    The first MUSES task is object detection.
                    The required output for each scenes is a set of bounding boxes.
                    Each bounding box is assigned a label from the "thing" classes,
                    along with a confidence score that ranges from 0 to 1.
                  </p>
                  <p>
                    Evaluation is performed using the standard average precision (AP) metric at the level of classes and averaging over all "things" classes. 
                  </p>
                </div>
              </div>
              <div class="row">
                <ResultTables
                  :tasks="tasksObjDet"
                  :isDetection="true"
                  :additional-headers="additionalHeaders"
                  :has-code="true"
                ></ResultTables>
              </div>
            </div>
          </section>
          <!-- <section class="page-section">
            <div class="container">
              <div class="row ">
                <div class="col-12">
                  <h4 class="text-black mb-0">
                    References
                  </h4>
                  <hr
                    class="divider dark m-0 mb-1"
                    style="max-width: 100% !important;"
                  />
                </div>
                <div class="col-12">
                  <p id="mgcdauiou">
                    [1] Christos Sakaridis, Dengxin Dai, and Luc Van Gool,
                    "Map-Guided Curriculum Domain Adaptation and
                    Uncertainty-Aware Evaluation for Semantic Nighttime Image
                    Segmentation," IEEE T-PAMI, 2020.
                  </p>
                </div>
              </div>
            </div>
          </section> -->
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import NavigationMain from "@/views/navigation/navigation-main";
import { mapActions } from "vuex";
import ResultTables from "@/views/benchmark/ResultTables";
import moment from "moment";
import { groupBy } from "@/helpers/utils";

export default {
  name: "SemSegPage.vue",
  components: {
    ResultTables,
    NavigationMain
  },

  methods: {
    submissionMapper(submission) {
      // eslint-disable-next-line
      submission.created_at = moment(submission.created_at).format(
        "MM/DD/YYYY HH:mm"
      );
      return submission;
    },
    groupBy,
    ...mapActions("api", ["getBenchmark"]),
    loadBenchmarks() {
      this.getBenchmark("semSeg").then(data => {
        this.items = data.submissions.map(this.submissionMapper);

        const semSeg = data.submissions.filter(e => e.challenge === "semSeg");
        const panSegUncertain = data.submissions.filter(e => e.challenge === "panSegUncertain");
        const objdet = data.submissions.filter(e => e.challenge === "objDet");
        const panseg = data.submissions.filter(e => e.challenge === "panSeg");

        this.tasksSemseg = this.groupBy(semSeg, "task");
        this.tasksPansegWithConfidence = this.groupBy(panSegUncertain, "task");
        this.tasksObjDet = this.groupBy(objdet, "task");
        this.tasksPanSeg = this.groupBy(panseg, "task");
      });
    }
  },
  created() {
    this.loadBenchmarks();
  },
  data() {
    return {
      tasksSemseg: { all: [1, 2, 3, 4, 5] },
      tasksPansegWithConfidence: { all: [1, 2, 3, 4, 5] },
      tasksObjDet: { all: [1, 2, 3, 4, 5] },
      tasksPanSeg: { all: [1, 2, 3, 4, 5] },
      items: [],
      additionalHeaders: [
        {
          text: "Code",
          align: "end",
          filterable: true,
          value: "code"
        }
      ]
    };
  }
};
</script>

<style scoped lang="scss">
.input-form {
  .row {
    padding-top: 2px;
    padding-bottom: 2px;
  }
}

.scroll-to-element {
  position: absolute;
  margin-top: -80px;
}
</style>
